import { FC, useEffect, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'

import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import {
  enteredTrackUrlEvent,
  startedEnterTrackEvent,
  toggleActivatedEvent,
} from 'src/Helpers/TagManager'
import { PublicRoutesEnum } from 'src/Router/PublicComponentsNewUI'
import { DEBOUNCE_TIMER_1000 } from 'src/Constants/constants'
import { useSearchSpotifyTrack } from 'src/Redux/spotifyTracksApi/useSearchSpotifyTrack'
import { useClearSpotifyTracksMutation } from 'src/Redux/spotifyTracksApi'

import { NoAuthPageLayout } from '../../Components/NoAuthPageLayout'
import { HelmetTitle } from '../../Components/HelmetTitle'
import { CreateCampaignBreadcrumbsNoAuth } from '../../Components/CreateCampaignBreadcrumbsNoAuth'
import { SearchedTrack, TrackSearch } from '../../Components/TrackSearch'
import { TrackSearchError } from '../../Components/TrackSearchError'
import { mapSpotifyTrack } from '../../helper'

const ContentContainer = styled.div`
  display: flex;
  max-width: 1280px;
  flex-direction: column;
  gap: 80px;
  align-items: center;
`

const TopContentContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 960px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`

export const SearchSpotifyTrackNoAuth: FC = () => {
  const navigate = useNavigate()
  const { search } = useCustomSearchParams()

  const {
    searchTracks,
    tracks,
    loading: isLoadingSpotify,
    error,
  } = useSearchSpotifyTrack()
  const [clearSpotifyTracks] = useClearSpotifyTracksMutation()

  useEffect(() => {
    toggleActivatedEvent({
      platformName: CampaignPlatformTypeEnum.SPOTIFY,
      campaignType: CampaignTypeEnum.FIRST,
      campaignNumber: 1,
    })
  }, [])

  const formattedTracks = useMemo<SearchedTrack[]>(
    () => tracks?.map(mapSpotifyTrack) || [],
    [tracks],
  )

  const onOutsideClick = (): void => {
    clearSpotifyTracks()
  }
  const debouncedFindTrack = useDebouncedCallback((searchInput: string) => {
    searchTracks(searchInput)

    const eventsParams = {
      campaignPlatform: CampaignPlatformTypeEnum.SPOTIFY,
      userId: null,
      isFirstCampaign: true,
      campaignNumber: 1,
    }
    if (searchInput.includes('https://')) {
      enteredTrackUrlEvent(eventsParams)
    } else {
      startedEnterTrackEvent(eventsParams)
    }
  }, DEBOUNCE_TIMER_1000)

  const onSearch = (searchInput: string): void => {
    if (searchInput === '') {
      debouncedFindTrack.cancel()
      clearSpotifyTracks()
    } else {
      debouncedFindTrack(searchInput)
    }
  }

  const onItemClick = (trackId: string): void => {
    navigate(
      `${PublicRoutesEnum.CreateSpotifyCampaign}/${trackId}?${search.toString()}`,
    )
  }

  return (
    <NoAuthPageLayout>
      <HelmetTitle />

      <ContentContainer>
        <TopContentContainer>
          <CreateCampaignBreadcrumbsNoAuth
            platform={CampaignPlatformTypeEnum.SPOTIFY}
          />
          <TrackSearch
            platform={CampaignPlatformTypeEnum.SPOTIFY}
            isLoading={isLoadingSpotify}
            onItemClick={onItemClick}
            tracks={formattedTracks}
            onSearch={onSearch}
            onOutsideClick={onOutsideClick}
            error={error && <TrackSearchError>{error}</TrackSearchError>}
          />
        </TopContentContainer>
      </ContentContainer>
    </NoAuthPageLayout>
  )
}
